import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { fetchAuthenticated } from '@/utils/fetch'
import {
  selectUtility,
  createUtility,
  CREATE_SUCCESS as CREATE_UTILITY_SUCCESS,
} from '@/actions/utilities'
import {
  selectPlan,
  createPlanFromExtraction,
  CREATE_PLAN_FROM_EXTRACTION_SUCCESS,
} from '@/actions/plans'
import { RSAAResultAction } from 'redux-api-middleware'
import { useSelectedUtility } from '@/hooks/useSelectedUtility'
import { logEvent } from '@/logging'
import { utilityCollection } from '@/reducers/utilityCollection'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import { useSelectedPlanFetcher } from '@/authenticated/hooks/plans'
import { Utility, Plan } from '@/types'
import { UtilityProgram } from '@/types/utilityProgram'
import { useBillUploadForSelection } from './useBillUploadForSelection'
import { usePlanSeasonalRateSummary } from './usePlanSeasonalRateSummary'

const FormSchema = z.object({
  utility: z.object({ id: z.number(), name: z.string() }).nullish(),
  plan: z.object({ id: z.number(), name: z.string() }).nullish(),
})

export enum EligibilityStatus {
  ELIGIBLE = 'ELIGIBLE',
  INELIGIBLE = 'INELIGIBLE',
  NONE = 'NONE',
}

type SelectedUtility = { id: number; name: string }

export type UtilityPlanSelectionParams = {
  /** When present, enables program eligibility gating; absent in profile mode. */
  utilityProgram?: UtilityProgram
  /** Whether the program requires a plan (enrollment); ignored in profile mode. */
  planRequired?: boolean
  /** Persist the selection then advance the flow (enrollment) or go to account-number (profile). */
  onContinue: () => void
  /** Enrollment-only external utility-connection requirement state. */
  hasExternalUtilityConnectionRequirement?: boolean
  hasCompletedExternalUtilityConnectionRequirement?: boolean
  onExternalUtilityConnectButtonClick?: (utility: Utility) => Promise<void>
  /** Opens the manual "add a plan" flow (item 3). */
  onAddPlan?: () => void
}

export function useUtilityPlanSelectionViewModel({
  utilityProgram,
  planRequired = false,
  onContinue,
  hasExternalUtilityConnectionRequirement = false,
  hasCompletedExternalUtilityConnectionRequirement = false,
  onExternalUtilityConnectButtonClick,
  onAddPlan,
}: UtilityPlanSelectionParams) {
  const dispatch = useAppDispatch()
  const { apiDispatch } = useApiDispatch()
  const user = useAppSelector((state) => state.user.user)
  const isFetchingUser = useAppSelector((state) => state.user.isFetchingUser)
  const utilityRefId = user?.profile.utility_ref_id ?? undefined
  // Whether the profile already has a utility AND plan saved — used so the
  // "bill on file" notice only appears for users who've actually set these up.
  const hasSavedUtilityAndPlan =
    user?.profile?.utility != null && user?.profile?.plan != null

  const { data: initialSelectedUtility } = useSelectedUtility()
  const { selectedPlan: defaultPlan } = useSelectedPlanFetcher()

  const [selectedUtility, setSelectedUtility] = useState<
    SelectedUtility | undefined
  >(
    initialSelectedUtility
      ? { id: initialSelectedUtility.id, name: initialSelectedUtility.name }
      : undefined,
  )
  const [planOptions, setPlanOptions] = useState<Plan[]>(
    initialSelectedUtility?.plan_options ?? [],
  )
  const [fetchingPlans, setFetchingPlans] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState<number | undefined>(
    defaultPlan?.id ?? undefined,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isBuilding, setIsBuilding] = useState(false)
  const [buildError, setBuildError] = useState<string | null>(null)
  // True once a plan has been built from the extraction this session.
  const [planBuilt, setPlanBuilt] = useState(false)
  // Becomes true once the saved utility/plan have been read off the profile and
  // (when present) their plan options fetched — used to gate the initial loader.
  const [profileInitDone, setProfileInitDone] = useState(false)
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  const upload = useBillUploadForSelection({
    utilityProgramId: utilityProgram?.id,
  })

  const { seasonalRateSummary, isLoading: ratesLoading } =
    usePlanSeasonalRateSummary(selectedPlanId)

  // --- Eligibility (only meaningful inside enrollment, where a program exists) ---
  const utilityEligibilityStatus: EligibilityStatus = useMemo(() => {
    if (!utilityProgram || !selectedUtility) {
      return EligibilityStatus.NONE
    }
    return utilityProgram.utility_ids.includes(selectedUtility.id)
      ? EligibilityStatus.ELIGIBLE
      : EligibilityStatus.INELIGIBLE
  }, [selectedUtility, utilityProgram])

  const planEligibilityStatus: EligibilityStatus = useMemo(() => {
    if (
      !utilityProgram ||
      !planRequired ||
      !selectedPlanId ||
      !selectedUtility
    ) {
      return EligibilityStatus.NONE
    }
    return utilityProgram.utility_plans.includes(selectedPlanId)
      ? EligibilityStatus.ELIGIBLE
      : EligibilityStatus.INELIGIBLE
  }, [selectedUtility, utilityProgram, selectedPlanId, planRequired])

  // --- Utility search ---
  const [utilities, setUtilities] = useState<Utility[]>([])
  const options = useMemo(
    () => utilities.map((u) => ({ id: u.id, name: u.name })),
    [utilities],
  )
  const [utilitySearchLoading, setUtilitySearchLoading] = useState(false)

  const onQueryChange = async (value: string) => {
    if (value.length === 0) {
      return
    }
    const searchParams = new URLSearchParams([['search', value]])
    setUtilitySearchLoading(true)
    const resp = await fetchAuthenticated(
      `/search-utilities`,
      undefined,
      searchParams,
    )
    if (!resp.ok) {
      setUtilitySearchLoading(false)
      logEvent(
        `${resp.status} response searching for utilities: ${await resp.text()}`,
      )
      return
    }
    setUtilitySearchLoading(false)
    const json = (await resp.json()) as Utility[]
    setUtilities(json.slice(0, 4))
  }

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    mode: 'onTouched',
  })

  // Fetch plan options for a utility id; returns the fetched Utility (or null).
  const fetchUtility = useCallback(
    async (utilityId: number): Promise<Utility | null> => {
      setFetchingPlans(true)
      const res = await apiDispatch<Utility>(
        utilityCollection.actions.fetch({ id: utilityId }),
      )
      setFetchingPlans(false)
      return res.ok ? res.data : null
    },
    [apiDispatch],
  )

  // User-driven utility change from the combo-box (via react-hook-form watch).
  const utilityChangeHandler = useCallback(
    async (value: { utility?: unknown }) => {
      const utility = value.utility as SelectedUtility | undefined
      setSelectedUtility(utility)
      // Changing the utility invalidates any previously-selected plan.
      setSelectedPlanId(undefined)
      setBuildError(null)
      if (!utility) {
        setPlanOptions([])
        return
      }
      const fetched = await fetchUtility(utility.id)
      setPlanOptions(fetched?.plan_options ?? [])
    },
    [fetchUtility],
  )

  useEffect(() => {
    const { unsubscribe } = form.watch(utilityChangeHandler)
    return () => unsubscribe()
  }, [form, utilityChangeHandler])

  // Programmatic selection (bill match / build / manual add) — bypasses the form
  // so it doesn't double-fetch through the watch handler. The combo-box re-mounts
  // via its `key` (keyed on the selected utility id) to display the new value.
  const applyUtilityAndPlan = useCallback(
    async (
      utilityId: number,
      planId?: number,
      extraPlanOption?: Plan,
    ): Promise<void> => {
      const fetched = await fetchUtility(utilityId)
      const name = fetched?.name ?? ''
      setSelectedUtility({ id: utilityId, name })
      let nextOptions = fetched?.plan_options ?? []
      if (
        extraPlanOption &&
        !nextOptions.some((p) => p.id === extraPlanOption.id)
      ) {
        nextOptions = [extraPlanOption, ...nextOptions]
      }
      setPlanOptions(nextOptions)
      setSelectedPlanId(planId)
    },
    [fetchUtility],
  )

  // On a fresh page load / refresh the saved utility & plan arrive only after the
  // first render, so the useState initializers above miss them. Seed the dropdowns
  // from the profile once it loads — but only when nothing has been selected yet,
  // so we never clobber a user- or bill-driven selection.
  const didInitFromProfile = useRef(false)
  useEffect(() => {
    if (didInitFromProfile.current || !user) {
      return
    }
    didInitFromProfile.current = true
    const profileUtility = user.profile?.utility
    // Nothing to seed, or already seeded (warm store) — init is complete.
    if (!profileUtility || selectedUtility) {
      setProfileInitDone(true)
      return
    }
    const profilePlan = user.profile?.plan
    void applyUtilityAndPlan(
      profileUtility.id,
      profilePlan?.id,
      profilePlan ?? undefined,
    ).finally(() => setProfileInitDone(true))
  }, [user, selectedUtility, applyUtilityAndPlan])

  // Show the initial loader until the user, the seeded utility/plan, and (when a
  // plan is selected) its rates have all finished loading — then latch so later
  // plan changes don't re-trigger the full-page loader.
  const initialReady =
    user != null && !isFetchingUser && profileInitDone && !ratesLoading
  useEffect(() => {
    if (!initialLoadDone && initialReady) {
      setInitialLoadDone(true)
    }
  }, [initialLoadDone, initialReady])
  const isInitializing = !initialLoadDone

  // React to a fresh bill upload: pre-fill the dropdowns from a matched utility/plan.
  useEffect(() => {
    if (upload.resultToken === 0) {
      return
    }
    setBuildError(null)
    setPlanBuilt(false)
    // No plan matched but the bill has a rate schedule → build the plan for the
    // user automatically (there's no manual "build" button to press).
    if (upload.outcome === 'buildable') {
      void buildPlan()
      return
    }
    const matchedUtility = upload.extraction?.matched_utility
    if (matchedUtility != null) {
      void applyUtilityAndPlan(
        matchedUtility,
        upload.extraction?.matched_plan ?? undefined,
      )
    }
    // Only respond to a new upload result, not to applyUtilityAndPlan identity.
  }, [upload.resultToken])

  // --- Build the plan from the bill (auto-triggered for a "buildable" upload) ---
  // The extraction to build from: a fresh in-session upload, or one already
  // stored on the profile (earlier upload / step).
  const activeExtraction = upload.extraction ?? upload.persistedExtraction
  const buildPlan = async () => {
    const extractionId = activeExtraction?.id
    if (extractionId == null) {
      return
    }
    setIsBuilding(true)
    setBuildError(null)
    const res = (await dispatch(
      createPlanFromExtraction(extractionId),
    )) as RSAAResultAction
    if (res.type === CREATE_PLAN_FROM_EXTRACTION_SUCCESS) {
      const plan = (res as { payload?: Plan }).payload
      if (plan) {
        await applyUtilityAndPlan(plan.utility, plan.id, plan)
      }
      setPlanBuilt(true)
      // Keep the loader up until the new plan's seasonal rates have loaded too
      // (cleared by the effect below) so the result + rate preview appear together.
    } else {
      setBuildError(
        "We couldn't build your plan from the bill. Please try again or add your utility and plan manually below.",
      )
      setIsBuilding(false)
    }
  }

  // The build isn't "done" until the created plan's rates have finished loading.
  useEffect(() => {
    if (isBuilding && planBuilt && !ratesLoading) {
      setIsBuilding(false)
    }
  }, [isBuilding, planBuilt, ratesLoading])

  // --- Manual add (item 3) ---
  const addUtility = async (name: string): Promise<void> => {
    const res = (await dispatch(createUtility({ name }))) as RSAAResultAction
    if (res.type === CREATE_UTILITY_SUCCESS) {
      const utility = (res as { payload?: Utility }).payload
      if (utility) {
        setSelectedUtility({ id: utility.id, name: utility.name })
        setPlanOptions(utility.plan_options ?? [])
        setSelectedPlanId(undefined)
      }
    }
  }

  // --- Continue ---
  const onNextClick = async () => {
    if (!selectedUtility?.id) {
      return
    }
    if (planRequired && !selectedPlanId) {
      return
    }
    setIsSaving(true)
    await dispatch(selectUtility({ utility_id: selectedUtility.id }))
    if (selectedPlanId) {
      await dispatch(selectPlan({ plan_id: selectedPlanId }))
    }
    setIsSaving(false)
    onContinue()
  }

  const isBusy =
    isSaving ||
    isBuilding ||
    fetchingPlans ||
    upload.fileUploadStatus === 'loading'

  // Surface the selected plan in the dropdown even when it isn't one of the
  // utility's standard plan_options — e.g. a just-created manual/custom plan
  // returned from the "+ Add my own plan" flow (state.plans.selectedPlan).
  const planOptionsForDisplay = useMemo(() => {
    if (
      defaultPlan &&
      selectedPlanId === defaultPlan.id &&
      !planOptions.some((option) => option.id === defaultPlan.id)
    ) {
      return [defaultPlan, ...planOptions]
    }
    return planOptions
  }, [planOptions, defaultPlan, selectedPlanId])

  return {
    form,
    options,
    initialUtility: selectedUtility,
    selectedUtility,
    utilityEligibilityStatus,
    planEligibilityStatus,
    onQueryChange,
    onNextClick,
    /** Advance the flow without selecting a utility/plan (team creates it from the bill). */
    onSkip: onContinue,
    planRequired,
    planOptions: planOptionsForDisplay,
    fetchingPlans,
    selectedPlanId,
    setSelectedPlanId,
    isBuilding,
    isBusy,
    buildError,
    utilityProgram,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
    onExternalUtilityConnectButtonClick,
    initialSelectedUtility,
    utilityRefId,
    utilitySearchLoading,
    // Bill upload
    upload,
    /** True once a plan has been built from the bill this session. */
    planBuilt,
    // Manual add
    addUtility,
    onAddPlan,
    // Rate preview
    seasonalRateSummary,
    /** True until the user, seeded utility/plan, and initial rates have loaded. */
    isInitializing,
    /** True when the profile already has both a utility and a plan saved. */
    hasSavedUtilityAndPlan,
  }
}
